import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { ApiError } from '@/types/api.types';
import { BusinessSettingsLayout } from '@components/layout/BusinessSettingsLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { mapSettingsError, useBusinessProfile } from '@features/settings';
import type { BusinessProfilePatch } from '@features/settings';
import { cn } from '@/styles/tailwind-utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ViewField({
  label,
  value,
  className,
}: {
  label: string;
  value?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type FormValues = {
  businessName: string;
  legalName: string;
  bookingSubdomain: string;
  businessDescription: string;
  taxRegistrationNumber: string;
  taxRegistrationType: string;
};

export function BusinessDetailsPage() {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const { profile, isLoading, fetchError, update, isUpdating, updateError } =
    useBusinessProfile();

  const schema = useMemo(
    () =>
      z.object({
        businessName: z
          .string()
          .min(1, t('settings.businessDetails.errors.businessNameRequired'))
          .max(100),
        legalName: z.string().max(150),
        bookingSubdomain: z
          .string()
          .min(3, t('settings.businessDetails.errors.subdomainMin'))
          .max(63, t('settings.businessDetails.errors.subdomainMax'))
          .regex(/^[a-z0-9-]+$/, t('settings.businessDetails.errors.subdomainInvalid')),
        businessDescription: z
          .string()
          .max(500, t('settings.businessDetails.errors.descriptionMax')),
        taxRegistrationNumber: z.string().max(20),
        taxRegistrationType: z.string(),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      businessName: '',
      legalName: '',
      bookingSubdomain: '',
      businessDescription: '',
      taxRegistrationNumber: '',
      taxRegistrationType: '',
    },
  });

  const resetForm = (source: typeof profile) => {
    form.reset({
      businessName: source?.businessName ?? '',
      legalName: source?.legalName ?? '',
      bookingSubdomain: source?.bookingSubdomain ?? '',
      businessDescription: source?.businessDescription ?? '',
      taxRegistrationNumber: source?.tax?.taxRegistrationNumber ?? '',
      taxRegistrationType: source?.tax?.taxRegistrationType ?? '',
    });
  };

  useEffect(() => {
    if (profile && !isEditing) resetForm(profile);
  }, [profile]);

  const handleEdit = () => {
    resetForm(profile);
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetForm(profile);
    setIsEditing(false);
  };

  const onSubmit = form.handleSubmit((values) => {
    const patch: BusinessProfilePatch = {
      businessName: values.businessName,
      bookingSubdomain: values.bookingSubdomain,
      legalName: values.legalName || undefined,
      businessDescription: values.businessDescription || undefined,
    };

    const hasTax = values.taxRegistrationNumber || values.taxRegistrationType;
    if (hasTax) {
      patch.tax = {
        taxRegistrationNumber: values.taxRegistrationNumber || undefined,
        taxRegistrationType: values.taxRegistrationType === 'VAT' ? 'VAT' : undefined,
      };
    }

    update(patch, {
      onSuccess: () => setIsEditing(false),
      onError: (err: ApiError) => {
        if (err.code === 'SUBDOMAIN_UNAVAILABLE') {
          form.setError('bookingSubdomain', {
            message: t('settings.businessDetails.errors.subdomainUnavailable'),
          });
        }
        if (err.code === 'VALIDATION_ERROR' && err.fields) {
          for (const [field, message] of Object.entries(err.fields)) {
            form.setError(field as keyof FormValues, { message });
          }
        }
      },
    });
  });

  const { errors, isDirty } = form.formState;
  const showGenericError =
    updateError !== null &&
    updateError?.code !== 'SUBDOMAIN_UNAVAILABLE' &&
    updateError?.code !== 'VALIDATION_ERROR';

  return (
    <BusinessSettingsLayout>
      {isLoading ? (
        <BrandLoader
          title={t('settings.loading.title')}
          subtitle={t('settings.loading.subtitle')}
        />
      ) : fetchError ? (
        <div className="rounded-md border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-destructive">
            {mapSettingsError(fetchError, t, 'settings.businessDetails.errors.loadFailed')}
          </p>
        </div>
      ) : isEditing ? (
        // ── Edit mode ─────────────────────────────────────────────────────────
        <form onSubmit={onSubmit} noValidate>
          <div className="rounded-md border border-border bg-surface">
            {/* Header */}
            <div className="border-b border-border px-4 py-3 sm:px-6 sm:py-4">
              <h2 className="text-base font-semibold text-foreground">
                {t('settings.businessDetails.title')}
              </h2>
            </div>

            {/* Business information */}
            <div className="px-4 py-5 sm:px-6 sm:py-6">
              <h3 className="mb-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('settings.businessDetails.businessInfo')}
              </h3>
              <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label={t('settings.businessDetails.fields.businessName')}
                    {...form.register('businessName')}
                    error={errors.businessName?.message}
                    disabled={isUpdating}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Input
                    label={t('settings.businessDetails.fields.legalName')}
                    {...form.register('legalName')}
                    error={errors.legalName?.message}
                    helperText={t('settings.businessDetails.fields.legalNameHelper')}
                    disabled={isUpdating}
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-foreground">
                      {t('settings.businessDetails.fields.bookingURL')}
                    </label>
                    <div
                      className={cn(
                        'flex items-center rounded-sm border border-input bg-surface',
                        'focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-1',
                        isUpdating && 'cursor-not-allowed opacity-50',
                        errors.bookingSubdomain && 'border-destructive focus-within:ring-destructive',
                      )}
                    >
                      <span className="select-none whitespace-nowrap pl-3 text-sm text-muted-foreground">
                        https://
                      </span>
                      <input
                        type="text"
                        aria-label={t('settings.businessDetails.fields.bookingURL')}
                        className="h-10 flex-1 bg-transparent px-1 text-sm focus:outline-none disabled:cursor-not-allowed"
                        {...form.register('bookingSubdomain')}
                        disabled={isUpdating}
                      />
                      <span className="select-none whitespace-nowrap pr-3 text-sm text-muted-foreground">
                        .calendo.app
                      </span>
                    </div>
                    {errors.bookingSubdomain ? (
                      <p role="alert" className="text-sm text-destructive">
                        {errors.bookingSubdomain.message}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t('settings.businessDetails.fields.bookingURLHelper')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-foreground">
                      {t('settings.businessDetails.fields.businessDescription')}
                    </label>
                    <textarea
                      {...form.register('businessDescription')}
                      rows={4}
                      disabled={isUpdating}
                      className={cn(
                        'w-full resize-y rounded-sm border border-input bg-surface px-3 py-2 text-sm',
                        'placeholder:text-muted-foreground',
                        'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        errors.businessDescription && 'border-destructive focus:ring-destructive',
                      )}
                    />
                    {errors.businessDescription && (
                      <p role="alert" className="text-sm text-destructive">
                        {errors.businessDescription.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Tax information */}
            <div className="px-4 py-5 sm:px-6 sm:py-6">
              <h3 className="mb-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('settings.businessDetails.taxInfo')}
              </h3>
              <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                <div>
                  <Input
                    label={t('settings.businessDetails.fields.taxRegistrationNumber')}
                    {...form.register('taxRegistrationNumber')}
                    error={errors.taxRegistrationNumber?.message}
                    disabled={isUpdating}
                  />
                </div>

                <div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-foreground">
                      {t('settings.businessDetails.fields.taxRegistrationType')}
                    </label>
                    <select
                      {...form.register('taxRegistrationType')}
                      disabled={isUpdating}
                      className={cn(
                        'h-10 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm',
                        'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                    >
                      <option value="">
                        {t('settings.businessDetails.fields.noTaxType')}
                      </option>
                      <option value="VAT">
                        {t('settings.businessDetails.fields.vatOption')}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-y-3 border-t border-border px-4 py-3 sm:px-6 sm:py-4">
              <div>
                {showGenericError ? (
                  <p className="text-sm text-destructive">
                    {mapSettingsError(updateError!, t, 'settings.businessDetails.errors.saveFailed')}
                  </p>
                ) : isDirty ? (
                  <p className="text-sm text-muted-foreground">
                    {t('settings.unsavedChanges')}
                  </p>
                ) : null}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  disabled={isUpdating}
                >
                  {t('settings.businessDetails.cancel')}
                </Button>
                <Button type="submit" disabled={!isDirty} isLoading={isUpdating}>
                  {t('settings.businessDetails.save')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      ) : (
        // ── View mode ─────────────────────────────────────────────────────────
        <div className="rounded-md border border-border bg-surface">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('settings.businessDetails.title')}
            </h2>
            <Button variant="secondary" size="sm" onClick={handleEdit}>
              {t('settings.businessDetails.edit')}
            </Button>
          </div>

          {/* Business information */}
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <h3 className="mb-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.businessDetails.businessInfo')}
            </h3>
            <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
              <ViewField
                label={t('settings.businessDetails.fields.businessName')}
                value={profile?.businessName}
                className="sm:col-span-2"
              />
              <ViewField
                label={t('settings.businessDetails.fields.legalName')}
                value={profile?.legalName}
                className="sm:col-span-2"
              />
              <div>
                <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('settings.businessDetails.fields.bookingURL')}
                </dt>
                <dd className="mt-1.5 text-sm">
                  {profile?.bookingURL ? (
                    <a
                      href={profile.bookingURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand hover:underline"
                    >
                      {profile.bookingURL}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <ViewField
                label={t('settings.businessDetails.fields.businessDescription')}
                value={profile?.businessDescription}
                className="sm:col-span-2"
              />
            </dl>
          </div>

          <div className="border-t border-border" />

          {/* Tax information */}
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <h3 className="mb-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('settings.businessDetails.taxInfo')}
            </h3>
            <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
              <ViewField
                label={t('settings.businessDetails.fields.taxRegistrationNumber')}
                value={profile?.tax?.taxRegistrationNumber}
              />
              <ViewField
                label={t('settings.businessDetails.fields.taxRegistrationType')}
                value={
                  profile?.tax?.taxRegistrationType
                    ? t('settings.businessDetails.fields.vatOption')
                    : undefined
                }
              />
            </dl>
          </div>
        </div>
      )}
    </BusinessSettingsLayout>
  );
}
